import { Button, Empty, Modal, notification, Spin, Tabs, TabsProps } from 'antd';
import React, { useEffect, useState } from 'react';
import { SwapOutlined } from '@ant-design/icons';
import { getAccessToken } from '../../api/api';
import { useApi } from '../../api/useApi';
import { ContractorApiListSharedType } from '../../types';
import { useAppSelector } from '../../utilities/hooks';
import { MySharedDocumentsTabManager } from './my-shared-documents-tab-manager';
import { MySharedDocumentsTabByRecipient } from './my-shared-documents-tab-by-recipient';
import { MySharedDocumentsTabByResource } from './my-shared-documents-tab-by-resource';

const EMPTY_LIST_DATA: ContractorApiListSharedType = {
    listShare: [],
    listShareItem: [],
    listFolder: [],
    listDocument: [],
    listRecipient: [],
    listSharer: [],
};

const getDocumentsApiBaseUrl = () => {
    // Direct process.env access is inlined by CRA and bypasses the per-customer deployment replacement.
    const configuredUrl = Reflect.get(process.env, 'REACT_APP_DOCUMENTS_API_URL');

    return (configuredUrl ?? window.location.origin).replace(/\/+$/, '');
};

const fetchManagerSharedData = async () => {
    const token = await getAccessToken();
    const response = await fetch(`${getDocumentsApiBaseUrl()}/list-shared?scope=manager`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        credentials: 'omit',
    });

    if (!response.ok) {
        throw new Error(`Failed to load manager shared documents: ${response.status}`);
    }

    return response.json();
};

export const MySharedDocumentsModal = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState<ContractorApiListSharedType>();
    const [managerData, setManagerData] = useState<ContractorApiListSharedType>();
    const [updateData, setUpdateData] = useState<boolean>(false);

    const api = useApi();
    const permissions = useAppSelector((state) => state.auth.permissions);
    const hasDocumentSharingManagerPermission =
        permissions.includes('files.manage') && permissions.includes('files.project.manage');

    const [notificationApi, contextHolder] = notification.useNotification();

    useEffect(() => {
        (async () => {
            if (isModalOpen && updateData) {
                setIsLoading(true);

                try {
                    const [ownResult, managerResult] = await Promise.all([
                        api.documents.listShared().then((response) => response.json()),
                        hasDocumentSharingManagerPermission ? fetchManagerSharedData() : Promise.resolve(null),
                    ]);

                    setData(ownResult.data);
                    setManagerData(managerResult?.data);
                } catch (error) {
                    console.error('Failed to load shared documents:', error);
                    notificationApi.error({
                        message: 'Failed to load shared documents',
                    });
                } finally {
                    setUpdateData(false);
                    setIsLoading(false);
                }
            }
        })();
    }, [api, hasDocumentSharingManagerPermission, isModalOpen, notificationApi, updateData]);

    const isNotEmpty = data ? data.listRecipient.length && (data.listFolder.length || data.listDocument.length) : false;
    const isManagerNotEmpty = managerData ? managerData.listShareItem.length : false;

    const items: TabsProps['items'] = [
        {
            key: '1',
            label: 'by Recipient',
            children: isNotEmpty ? (
                <MySharedDocumentsTabByRecipient setUpdateData={setUpdateData} listData={data ?? EMPTY_LIST_DATA} />
            ) : (
                <Empty />
            ),
        },
        {
            key: '2',
            label: 'by Resource',
            children: isNotEmpty ? (
                <MySharedDocumentsTabByResource setUpdateData={setUpdateData} listData={data ?? EMPTY_LIST_DATA} />
            ) : (
                <Empty />
            ),
        },
        ...(hasDocumentSharingManagerPermission
            ? [
                  {
                      key: '3',
                      label: 'All shares',
                      children: isManagerNotEmpty ? (
                          <MySharedDocumentsTabManager
                              setUpdateData={setUpdateData}
                              listData={managerData ?? EMPTY_LIST_DATA}
                          />
                      ) : (
                          <Empty />
                      ),
                  },
              ]
            : []),
    ];

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    return (
        <>
            <Button
                type="text"
                onClick={() => {
                    setUpdateData(true);
                    setIsModalOpen(true);
                }}
                icon={<SwapOutlined />}
            >
                My shared
            </Button>

            {contextHolder}
            <Modal
                title="My shared documents and folders"
                open={isModalOpen}
                onCancel={handleCancel}
                zIndex={2000}
                width={hasDocumentSharingManagerPermission ? 1100 : 600}
                confirmLoading={isLoading}
                footer={null}
            >
                <Spin spinning={isLoading}>
                    <Tabs defaultActiveKey="1" items={items} onChange={() => {}} />
                </Spin>
            </Modal>
        </>
    );
};
